import { useCallback, useEffect, useState } from "react";
import { MdEdit, MdAdd, MdDeleteOutline } from "react-icons/md";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import TableComponent from "../../components/table/TableComponent";
import { DispatchType } from "../../interfaces/redux.interface";
import { Column } from "../../interfaces/table.interface";
import { TrainingVideoInterface } from "../../interfaces/trainingVideo.interface";
import { get, remove } from "../../redux/ApiHelper";
import { errorToast, successToast } from "../../utils/toast.util";
import CreateTrainingVideoModal from "./CreateTrainingVideoModal";

const TrainingVideo = () => {
  const dispatch: DispatchType = useDispatch();
  const [filteredTrainingVideo, setFilteredTrainingVideo] = useState<
    TrainingVideoInterface[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editData, setEditData] = useState<TrainingVideoInterface>();
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const getTrainingVideos = useCallback(async () => {
    dispatch(startLoading());
    try {
      const response = await get("/admin/training-video/all");
      setFilteredTrainingVideo(response.data);
    } catch {
      errorToast("Failed to fetch training videos");
    } finally {
      dispatch(stopLoading());
    }
  }, [dispatch]);

  useEffect(() => {
    void getTrainingVideos();
  }, [isEdit, getTrainingVideos]);

  const openModal = () => {
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
    setIsEdit(false);
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this training video?")) {
      return;
    }

    dispatch(startLoading());
    try {
      await remove(`/admin/training-video/delete/${id}`);
      successToast("Training video deleted successfully");
      await getTrainingVideos();
    } catch {
      errorToast("Failed to delete training video");
    } finally {
      dispatch(stopLoading());
    }
  };

  const columns: Column[] = [
    { select: "title", header: "Title", align: "left" },
    { select: "forWhom", header: "Tutorial is for" },
    { select: "description", header: "Video description" },
    { select: "videoUrl", header: "Video Url" },
    {
      header: "Edit",
      render: (value: boolean, rowData: TrainingVideoInterface) => (
        <div
          className="rounded-full bg-slate-200 p-2 text-yellow-500 cursor-pointer"
          onClick={() => {
            setEditData(rowData);
            setIsEdit(true);
            setIsModalOpen(true);
          }}
        >
          <MdEdit className="m-auto" />
        </div>
      ),
    },
    {
      header: "Delete",
      render: (value: boolean, rowData: TrainingVideoInterface) => (
        <div
          className="rounded-full bg-slate-200 p-2 text-red-500 cursor-pointer"
          onClick={() => void handleDelete(rowData._id)}
        >
          <MdDeleteOutline className="m-auto" />
        </div>
      ),
    },
  ];

  return (
    <div className="coupon table-container">
      <div className="title">Training videos</div>
      <div className="search-container mb-4" />

      <TableComponent columns={columns} data={filteredTrainingVideo} />

      <div
        onClick={openModal}
        className="fixed top-8 right-8 bg-yellow-500 text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-yellow-600"
      >
        <MdAdd />
      </div>

      <CreateTrainingVideoModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        isEdit={isEdit}
        editData={editData}
      />
    </div>
  );
};

export default TrainingVideo;
